import React from 'react';
import {
    Table,
    Tabs,
    Text,
    Switch,
    SwitchSizes,
    Avatar,
    AvatarSizes,
    AvatarShapes,
    mergeClassNames,
    EmptyState,
} from '@clockout-dev/design-elements';
import DateFilters from '../../common/DateFilters';
import RelativeTime from '../../reports/elements/RelativeTime';
import { NotificationSeverityPill } from '../../common/notificationSeverityPill';
import { NotificationsConstants, TYPE_LABELS, TYPE_TABS } from '../notifications.enums';
import tableStyles from '../../treasury/elements/styles/transactionsTable.module.scss';
import styles from '../styles/notificationsTable.module.scss';

/**
 * Presentational list for the Notifications screen: type tabs, unread /
 * date filters, and the history-variant table. All state and handlers come
 * from `NotificationsController` via props — no fetching, no navigation here.
 */
function NotificationsTable({
    typeTab,
    onTypeTabChange,
    unreadOnly,
    onUnreadOnlyChange,
    timeFrame,
    dateRange,
    onTimeFrameChange,
    onDateRangeChange,
    dataSource,
    loading,
    loadError,
    pagination,
    onChange,
    onRowClick,
}) {
    const typeTabMeta = TYPE_TABS.find((t) => t.key === typeTab);

    const pageInfo = pagination
        ? {
              current: pagination.current,
              page: pagination.current,
              perPage: pagination.pageSize,
              total: pagination.total,
          }
        : null;

    const isUnread = (record) => !record?.readAt;

    const columns = [
        {
            title: '',
            key: 'unread',
            width: 28,
            align: 'center',
            render: (_, record) =>
                isUnread(record) ? <span className={styles.unreadDot} role="img" aria-label="Unread" /> : null,
        },
        {
            title: 'Severity',
            dataIndex: 'severity',
            key: 'severity',
            width: 130,
            render: (severity) => <NotificationSeverityPill severity={severity} />,
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            width: 160,
            render: (type) => (
                <Text as="span" size="xs" className={tableStyles.idCell}>
                    {TYPE_LABELS[type] || type || '—'}
                </Text>
            ),
        },
        {
            title: 'Message',
            key: 'message',
            ellipsis: true,
            render: (_, record) => (
                <div className={styles.messageCell}>
                    <Text
                        as="span"
                        size="xs"
                        className={mergeClassNames([
                            styles.messageTitle,
                            isUnread(record) ? styles.messageTitleUnread : null,
                        ])}
                    >
                        {record.title || '—'}
                    </Text>
                    {record.body ? (
                        <Text as="span" size="xs" className={styles.messageBody} title={record.body}>
                            {record.body}
                        </Text>
                    ) : null}
                </div>
            ),
        },
        {
            title: 'Member',
            key: 'member',
            width: 220,
            render: (_, record) => {
                if (!record.clientId) {
                    return (
                        <Text as="span" size="xs" className={tableStyles.idCell}>
                            —
                        </Text>
                    );
                }
                return (
                    <Avatar
                        name={record.memberName || record.memberEmail || 'Unknown member'}
                        subtitle={record.memberEmail || undefined}
                        size={AvatarSizes.SMALL}
                        shape={AvatarShapes.ROUNDED}
                    />
                );
            },
        },
        {
            title: 'When',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 140,
            render: (value) => <RelativeTime value={value} className={tableStyles.idCell} />,
        },
    ];

    const emptyDescription = loadError
        ? "Couldn't load notifications. Try refreshing."
        : unreadOnly
          ? 'No unread notifications'
          : typeTab === NotificationsConstants.LIST_TYPE_DEFAULT
            ? 'No notifications yet'
            : `No ${(typeTabMeta?.label || typeTab).toLowerCase()} notifications`;

    return (
        <section className={styles.panel}>
            <div className={styles.toolbar}>
                <Tabs
                    activeKey={typeTab}
                    onChange={onTypeTabChange}
                    searchParamKey={NotificationsConstants.LIST_TYPE_PARAM}
                    items={TYPE_TABS.map(({ key, label }) => ({ key, label }))}
                />
                <div className={styles.filters}>
                    <Switch
                        text="Unread only"
                        size={SwitchSizes.SMALL}
                        checked={!!unreadOnly}
                        onChange={(checked) => onUnreadOnlyChange(checked)}
                        classNames={{ root: styles.unreadSwitch }}
                    />
                    <DateFilters
                        activeTimeFrame={timeFrame}
                        dateRange={dateRange}
                        onTimeFrameChange={onTimeFrameChange}
                        onDateRangeChange={onDateRangeChange}
                    />
                </div>
            </div>
            <Table
                rowKey="id"
                dataSource={dataSource}
                loading={loading}
                columns={columns}
                pageInfo={pageInfo}
                saveSearchParams
                onChange={onChange}
                rowClassName={(record) => (isUnread(record) ? styles.unreadRow : '')}
                onRow={(record) => ({
                    onClick: () => onRowClick(record),
                })}
                emptyState={<EmptyState title={emptyDescription} />}
                classNames={{
                    shell: tableStyles.tableWrapper,
                    root: mergeClassNames([tableStyles.table, styles.clickableRows]),
                }}
            />
        </section>
    );
}

export default NotificationsTable;
